import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import { IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ThinLayout } from "../Layouts/ThinLayout";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { StudentResultsView } from "../Components/SubmissionReview/StudentResultsView";

export const GET_SUBMISSION_REVIEW_RESULTS_PREVIEW = gql`
  query GetSubmissionReviewResultsPreview($activityId: ID!, $userId: ID!) {
    submissionReviewResultsPreview(activityId: $activityId, userId: $userId) {
      activityName
      activityDescription
      submissionText
      submissionFileUrl
      submissionGrade
      instructorFeedback
      reviews {
        reviewerName
        submitted
        answers {
          id
          itemId
          value
          text
        }
      }
      rubricItems {
        id
        label
        itemType
        scale
        order
      }
      criterionSummaries {
        itemId
        label
        averageScore
        scoreCount
      }
    }
  }
`;

/**
 * Instructor preview of a student's results page, exactly as that student
 * will see it — mirrors the survey ResultsPreview feature. Available before
 * release so instructors can vet what goes out.
 */
export function SubmissionReviewResultsPreview() {
  const { activityId, userId } = useParams<{
    activityId: string;
    userId: string;
  }>();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  const { data, loading } = useQuery(GET_SUBMISSION_REVIEW_RESULTS_PREVIEW, {
    variables: { activityId, userId },
    fetchPolicy: "network-only",
    onError: (err) => setError(String(t("Error loading preview")), err),
  });

  const results = data?.submissionReviewResultsPreview;

  if (loading) {
    return (
      <ThinLayout title={String(t("Loading..."))} loading={true}>
        <div />
      </ThinLayout>
    );
  }

  if (!results) {
    return (
      <ThinLayout title={String(t("Preview"))}>
        <Typography variant="h5" sx={{ mt: 4, textAlign: "center" }}>
          {t("No results found for this student.")}
        </Typography>
      </ThinLayout>
    );
  }

  return (
    // FL-312: the purple full-width secondary bar is the shared preview
    // bar for released results — same one Team Review's ResultsPreview uses.
    <ThinLayout
      title={String(t(results.activityName))}
      secondaryTitle={String(
        t("Previewing results as the student will see them."),
      )}
      secondaryActionComponent={
        <IconButton
          edge="end"
          aria-label="close"
          onClick={() => navigate(`/submissions/${activityId}/results`)}
        >
          <Close fontSize={"small"} />
        </IconButton>
      }
    >
      <StudentResultsView results={results} />
    </ThinLayout>
  );
}
